import {ChangeDetectionStrategy, Component, Injectable, input, linkedSignal, output, signal} from '@angular/core';
import {MatButton} from '@angular/material/button';
import {MatDialog} from '@angular/material/dialog';
import {TranslateModule} from '@ngx-translate/core';
import {Autentiseringsmetode, BenyttetAutentiseringsmetode, BrukerId, BrukerModell, BrukerRessursClient, DeaktivertMeldingstype, Endringstype, EnkelBrukerModell, GruppeMedlemModell, HendelseAggregat, ResetPassordSpesifikasjon} from 'felles-ng2/tjenester/ressurser/generertNg2HttpKlient';
import {AvatarKomponent} from 'web-ng2/delt/komponenter/avatar/avatar';
import {AdministrasjonMedlemKomponent} from '../administrasjon-medlem/administrasjon-medlem';
import {KobleBrukereDialogKomponent, KobleBrukereDialogRespons} from 'web-ng2/moduler/bruker/modale/koble-brukere-dialog/koble-brukere-dialog';
import {KreverTilgangsrolleBrukerDirektiv} from 'web-ng2/delt/direktiver/kreverTilgangsrolleBruker/kreverTilgangsrolleBruker';
import {firstValueFrom} from 'rxjs';
import {TittelVerdiKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi";
import {TittelVerdiSamlingKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi-samling";
import {sisteElementEtter} from "felles/tjenester/Strenger";
import {AdministrasjonBrukerHendesesloggKomponent} from "web-ng2/moduler/administrasjon/komponenter/administrasjon-bruker-hendeseslogg/administrasjon-bruker-hendeseslogg";

type EntitetAggregat ={
    opprettet: number;
    endret: number;
    slettet: number;
}
type BrukerStats = {
    moeter: EntitetAggregat;
    saker: EntitetAggregat;
    dokumenter: EntitetAggregat;
    oppgaver: EntitetAggregat;
    beslutninger: EntitetAggregat;
};


@Component({
    selector: 'administrasjon-bruker',
    templateUrl: 'administrasjon-bruker.html',
    styleUrl: 'administrasjon-bruker.scss',
    imports: [AdministrasjonMedlemKomponent, AvatarKomponent, KreverTilgangsrolleBrukerDirektiv, MatButton, TranslateModule, TittelVerdiKomponent, TittelVerdiSamlingKomponent, AdministrasjonBrukerHendesesloggKomponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdministrasjonBrukerKomponent {
    static ajsSelector = 'administrasjonBruker';

    bruker = input.required<EnkelBrukerModell>();
    readonly visningsbruker = linkedSignal(() => this.bruker());
    readonly viserDetaljer = signal(false);
    readonly detaljer = signal<BrukerModell | null>(null);
    readonly medlemskap = signal<GruppeMedlemModell[]>([]);
    readonly aggregater = signal<HendelseAggregat[]>([]);
    readonly senderPassordreset = signal(false);
    readonly passordresetSendt = signal(false);
    readonly brukereKoblet = output<KobleBrukereDialogRespons>();
    readonly stats = linkedSignal<BrukerStats>(() => {
        const stats = this.tomBrukerStats();
        this.aggregater().forEach(agg => this.populerEntitetTypeStats(stats, agg));
        return stats;
    });

    constructor(private avhengigheter: AdministrasjonBrukerAvhengigheter, private matDialog: MatDialog) {}

    async byttDetaljvisning() {
        this.viserDetaljer.update(vises => !vises);
        if (this.viserDetaljer() && this.detaljer()?.id !== this.visningsbruker().id) await this.lastDetaljer();
    }

    async sendPassordreset() {
        this.senderPassordreset.set(true);
        try {
            await this.avhengigheter.sendPassordreset(this.visningsbruker().epost);
            this.passordresetSendt.set(true);
        } finally {
            this.senderPassordreset.set(false);
        }
    }

    async visKobleBrukereDialog() {
        const dialog = KobleBrukereDialogKomponent.vis(this.matDialog, this.visningsbruker().id);
        const respons = await firstValueFrom(dialog.afterClosed());
        if (respons?.koblet) this.brukereKoblet.emit(respons);
    }

    autentiseringsmetoder(): string[] { return this.detaljer()?.autentiseringsmetoder.map(autentiseringsmetodeTekst) ?? []; }
    openIdAutentiseringsmetoder(): string[] { return this.detaljer()?.openIdAutentiseringsmetoder.map(openIdAutentiseringsmetoderTekst) ?? []; }
    deaktiverteMeldingstyper(): string[] { return this.detaljer()?.deaktiverteMeldingstyper.map(deaktivertMeldingstypeTekst) ?? []; }

    private async lastDetaljer() {
        const id = this.visningsbruker().id;
        const [bruker, medlemskap, aggregater] = await Promise.all([
            this.avhengigheter.hent(id), this.avhengigheter.medlemskap(id), this.avhengigheter.aggregater(id)
        ]);
        if (id !== this.visningsbruker().id) return;
        this.detaljer.set(bruker);
        this.medlemskap.set(medlemskap);
        this.aggregater.set(aggregater);
        // const stats = this.stats();
        // this.aggregater().forEach(agg=>this.populerEntitetTypeStats(stats, agg))
        // this.stats.set(stats);
    }

    private tomBrukerStats():BrukerStats {
        function tomEntitetAggregat():EntitetAggregat {
            return {
                opprettet:0,
                endret:0,
                slettet:0
            };
        }

        return {
            moeter: tomEntitetAggregat(),
            saker: tomEntitetAggregat(),
            dokumenter: tomEntitetAggregat(),
            oppgaver: tomEntitetAggregat(),
            beslutninger: tomEntitetAggregat()
        };
    }

    /**
     * følgende er ikke inkludert (men finnes i aggregat):
     * Gruppe
     * Medlem
     * Tema
     * Møtedeltaker
     * Sakstype
     * Bruker
     * MedlemsRolle
     */
    private populerEntitetTypeStats(stats:BrukerStats, aggregat: HendelseAggregat) {
        console.log("aggregat", aggregat);
        console.log("sisteElementEtter", sisteElementEtter(aggregat.entitetType, "."));
        switch (sisteElementEtter(aggregat.entitetType, ".")) {
            case "Møte":
                return this.populerEndringstypeStats(stats.moeter, aggregat);
            case "Sak":
                return this.populerEndringstypeStats(stats.saker, aggregat);
            case "Dokument":
                return this.populerEndringstypeStats(stats.dokumenter, aggregat);
            case "Oppgave":
                return this.populerEndringstypeStats(stats.oppgaver, aggregat);
            case "Beslutning":
                return this.populerEndringstypeStats(stats.beslutninger, aggregat);
        }
    }

    private populerEndringstypeStats(stats:EntitetAggregat, aggregat: HendelseAggregat) {
        switch (aggregat.endringstype) {
            case Endringstype.opprett:
                return stats.opprettet = aggregat.antall;
            case Endringstype.rediger:
                return stats.endret = aggregat.antall;
            case Endringstype.slett:
                return stats.slettet = aggregat.antall;
        }
    }


}

@Injectable({providedIn: 'root'})
export class AdministrasjonBrukerAvhengigheter {
    constructor(private ressurs: BrukerRessursClient) {}
    hent(id: BrukerId): Promise<BrukerModell> { return this.ressurs.hentBruker(id).somPromise(); }
    medlemskap(id: BrukerId): Promise<GruppeMedlemModell[]> { return this.ressurs.brukersGrupper(id).somPromise(); }
    aggregater(id: BrukerId): Promise<HendelseAggregat[]> { return this.ressurs.revisjonsloggAggregater(id).somPromise(); }
    sendPassordreset(epost: string): Promise<void> {
        const spesifikasjon: ResetPassordSpesifikasjon = {brukernavn: null, brukernavnEllerEpost: epost};
        return this.ressurs.ønskerNyttPassord(spesifikasjon).somPromise();
    }
}


function autentiseringsmetodeTekst(metode: Autentiseringsmetode): string {
    switch (metode) {
        case Autentiseringsmetode.passord: return 'Passord';
        case Autentiseringsmetode.totpOtp: return 'Kodegenerator';
        case Autentiseringsmetode.smsOtp: return 'Sms';
        case Autentiseringsmetode.criiptoOpenId: return 'E-Id';
    }
}

function openIdAutentiseringsmetoderTekst(metode: BenyttetAutentiseringsmetode): string {
    switch (metode) {
        case BenyttetAutentiseringsmetode.bankidNorsk: return 'Norsk Bankid';
        case BenyttetAutentiseringsmetode.bankidSvensk: return 'Svensk BankId';
        case BenyttetAutentiseringsmetode.passord: return 'Passord';
        case BenyttetAutentiseringsmetode.smsReservekode: return 'Sms Reservekode';
        case BenyttetAutentiseringsmetode.sms: return 'Sms';
        case BenyttetAutentiseringsmetode.totp: return 'Totp';
        case BenyttetAutentiseringsmetode.totpReserverkode: return 'Totp reservekode';
    }
}

function deaktivertMeldingstypeTekst(metode: DeaktivertMeldingstype): string {
    switch (metode) {
        case DeaktivertMeldingstype.forfalteOppgaver: return 'forfalteOppgaver';
        case DeaktivertMeldingstype.oppgaveTildelt: return 'oppgaveTildelt';
        case DeaktivertMeldingstype.sakTildelt: return 'sakTildelt';
    }
}



